import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormValues, employeeModel } from '~/entities/Employee'
import { Role } from '~/shared/api'
import {
  localeEnumOptions,
  timezoneCodeOptions,
} from '~/shared/config/constants'
import { getObject } from '~/shared/lib/utils'
import {
  FormButtons,
  formButtonsModel,
  FormProvider,
  TextInput,
  AsyncAutocompleteInput,
  AutocompleteInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'

export type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
}: FormProps) {
  const { t } = useTranslation()
  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = (!isEditing && !isCreating) || isSubmitting

  const form = useForm<FormValues>({
    resolver: zodResolver(employeeModel.formSchema),
    defaultValues,
  })

  const { watch } = form

  const selectedLocale = watch('locale')
  const selectedLocaleId = getObject(selectedLocale)?.id

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues)
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  return (
    <FormProvider form={form} onSuccess={onSuccess} canEdit={canEdit}>
      <Container>
        <ContainerTitle>{t('form.information')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <TextInput
              label='form.full name'
              name='name'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={6}>
            <TextInput
              label='form.E-mail'
              name='email'
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
        </Grid>
      </Container>

      <Container data-testid='container-timezone'>
        <ContainerTitle>{t('form.timezone')}</ContainerTitle>
        <Grid container spacing={2.5} alignItems='flex-end'>
          <Grid xs={6}>
            <AutocompleteInput
              name='timezone'
              placeholder='form.timezone'
              options={timezoneCodeOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
              disableClearable
              dataTestId='Часовой Пояс'
            />
          </Grid>
          <Grid xs={6}>
            <AutocompleteInput
              name='locale'
              label='form.localization'
              placeholder='form.select'
              options={localeEnumOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
              disableClearable
              startDecorator={
                selectedLocaleId ? (
                  <img
                    src={`/icons/${selectedLocaleId}.png`}
                    width='26px'
                    alt='icon flag'
                  />
                ) : null
              }
            />
          </Grid>
        </Grid>
      </Container>

      <Container data-testid='container-roles'>
        <ContainerTitle>{t('form.roles')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <AsyncAutocompleteInput
            name='rolesIds'
            placeholder='form.add role'
            fetchOptions={Role.fetchOptions}
            skeletonShow={isLoading}
            readOnly={disabled}
            multiple
            autocompleteXs={6}
            dataTestId='Роли'
          />
        </Grid>
      </Container>

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
    </FormProvider>
  )
}
